import { Injectable, signal, computed, DestroyRef, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { User } from '../models/user.model';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class UserService {
  private readonly API_URL = environment.apiUrl;
  private readonly destroyRef = inject(DestroyRef);

  private usersSignal = signal<User[]>([]);
  private currentUserSignal = signal<User | null>(null);
  private loadingSignal = signal(false);

  readonly users = this.usersSignal.asReadonly();
  readonly currentUser = this.currentUserSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();

  readonly isAdmin = computed(() => this.currentUserSignal()?.role === 'ADMIN');
  readonly isStudent = computed(() => this.currentUserSignal()?.role === 'STUDENT');

  private usersCountSignal = signal<number>(0);
  readonly usersCount = this.usersCountSignal.asReadonly();

  constructor(private http: HttpClient) {}

  loadUsersCount(): void {
    this.http.get<User[]>(`${this.API_URL}/admin/user`).pipe(
      map(users => users.length),
      tap(count => this.usersCountSignal.set(count)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  getMe(): Observable<User> {
    return this.http.get<{ id?: string; email: string; firstname?: string; firstName?: string; name?: string; lastName?: string; avatar?: string; role?: string; status?: string; createdAt?: Date }>(`${this.API_URL}/user/me`).pipe(
      map(userData => {
        const user: User = {
          id: userData.id || userData.email,
          email: userData.email,
          firstName: userData.firstname || userData.firstName,
          lastName: userData.name || userData.lastName,
          avatar: userData.avatar,
          role: userData.role,
          status: (userData.status as 'enabled' | 'disabled') || 'enabled',
          createdAt: userData.createdAt
        };
        this.currentUserSignal.set(user);
        return user;
      }),
      catchError(err => {
        throw err;
      })
    );
  }

  setCurrentUser(user: User | null): void {
    this.currentUserSignal.set(user);
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  }

  loadFromStorage(): void {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        this.currentUserSignal.set(JSON.parse(userStr));
      } catch {
        this.currentUserSignal.set(null);
      }
    }
  }

  getUsers(): Observable<User[]> {
    return this.http.get<{ id: string; email: string; firstName?: string; lastName?: string; avatar?: string; role?: string; status?: string; paymentDate?: string }[]>(`${this.API_URL}/admin/user`).pipe(
      map(users => users.map(u => ({
        id: u.id,
        email: u.email,
        firstName: u.firstName,
        lastName: u.lastName,
        avatar: u.avatar,
        role: u.role,
        status: (u.status as 'enabled' | 'disabled') || 'enabled',
        paymentDate: u.paymentDate
      }))),
      tap(users => this.usersSignal.set(users)),
      catchError(err => {
        throw err;
      })
    );
  }

  getUser(id: string): Observable<User> {
    return this.http.get<User>(`${this.API_URL}/user/${encodeURIComponent(id)}`);
  }

  updateUser(email: string, data: Partial<User>): Observable<User> {
    return this.http.patch<User>(`${this.API_URL}/user/${encodeURIComponent(email)}`, data).pipe(
      tap(user => {
        const currentUsers = this.usersSignal();
        const index = currentUsers.findIndex(u => u.id === user.id);
        if (index !== -1) {
          const updatedUsers = [...currentUsers];
          updatedUsers[index] = user;
          this.usersSignal.set(updatedUsers);
        }
        if (this.currentUserSignal()?.id === user.id) {
          this.currentUserSignal.set(user);
        }
      })
    );
  }

  deleteUser(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_URL}/user/${encodeURIComponent(id)}`).pipe(
      tap(() => {
        const currentUsers = this.usersSignal();
        this.usersSignal.set(currentUsers.filter(u => u.id !== id));
      })
    );
  }

  enableUser(id: string): Observable<User> {
    return this.http.put<User>(`${this.API_URL}/admin/user/${encodeURIComponent(id)}`, { status: 'enabled' });
  }

  createStripeCheckoutSession(): Observable<{ url: string; sessionId: string }> {
    return this.http.post<{ url: string; sessionId: string }>(`${this.API_URL}/stripe/create-session`, { email: this.currentUser()?.email });
  }

  enableUserWithPayment(sessionId: string): Observable<User> {
    return this.http.post<User>(`${this.API_URL}/user/enable-with-payment`, { sessionId });
  }

  disableUser(id: string): Observable<User> {
    return this.http.put<User>(`${this.API_URL}/admin/user/${encodeURIComponent(id)}`, { status: 'disabled' });
  }

  forgotPassword(email: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.API_URL}/auth/forgot-password`, { email });
  }

  resetPassword(data: { email: string; password: string; code: string }): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.API_URL}/auth/reset-password`, data);
  }

  logout(): void {
    this.currentUserSignal.set(null);
    localStorage.removeItem('user');
  }
}